import React, { Fragment, useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cn, formatDateTime } from '../../lib/utils';
import { NODE_TYPES, NODE_STATUSES } from './narrativeConstants';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  X, Edit2, Copy, Trash2, GitBranch, Lock, Star,
  MapPin, Users, Compass, Package, Clock, ChevronRight,
  ArrowLeft, MoreHorizontal, Sparkles, Eye, Tag, Plus,
  Target, Unlock, ArrowRight,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const QUEST_STATUS_COLORS = {
  unknown: '#6b7280',
  known: '#3b82f6',
  in_progress: '#f59e0b',
  completed: '#10b981',
  failed: '#ef4444',
  abandoned: '#6b7280',
};

const QUEST_STATUS_LABELS = {
  unknown: { fr: 'Inconnue', en: 'Unknown' },
  known: { fr: 'Connue', en: 'Known' },
  in_progress: { fr: 'En cours', en: 'In Progress' },
  completed: { fr: 'Terminée', en: 'Completed' },
  failed: { fr: 'Échouée', en: 'Failed' },
  abandoned: { fr: 'Abandonnée', en: 'Abandoned' },
};

export default function NarrativeNodeDetailsPanel({
  node, edges, allNodes, language = 'fr', isMJ,
  onClose, onEdit, onDuplicate, onDelete, onStatusChange, onSelectNode,
  quests = [], onCreateQuestFromNode,
}) {
  const { campaignId } = useParams();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!node) return null;
  const lang = language;
  const typeConfig = NODE_TYPES[node.node_type] || NODE_TYPES.encounter;
  const statusConfig = NODE_STATUSES[node.status] || NODE_STATUSES.not_started;
  const TypeIcon = typeConfig.icon;

  const outEdges = edges.filter(e => e.source_id === node.id);
  const inEdges = edges.filter(e => e.target_id === node.id);

  // Resolve linked quests with full data
  const linkedQuestsFull = (node.linked_quests || [])
    .map(qid => quests.find(q => q.id === qid))
    .filter(Boolean);

  const hasLinkedEntities = node.linked_npcs?.length || node.linked_locations?.length || node.linked_items?.length;

  const getNodeTitle = (id) => {
    const n = allNodes.find(n => n.id === id);
    return n?.title || id.slice(0, 8);
  };

  const getNodeType = (id) => {
    const n = allNodes.find(n => n.id === id);
    return NODE_TYPES[n?.node_type] || NODE_TYPES.encounter;
  };

  const panelContent = (
    <div className="flex flex-col h-full">
      {/* ── Hero header with color accent ── */}
      <div className="relative shrink-0 overflow-hidden">
        {/* Gradient background */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ background: `linear-gradient(135deg, ${typeConfig.color}, transparent 70%)` }}
        />
        <div className="relative px-5 pt-4 pb-4">
          {/* Top actions row */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg bg-background/60 backdrop-blur-sm border border-border/40 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-all"
            >
              <X className="h-4 w-4" />
            </button>

            {isMJ && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-8 w-8 rounded-lg bg-background/60 backdrop-blur-sm border border-border/40 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-all">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => onEdit(node)}>
                    <Edit2 className="h-3.5 w-3.5 mr-2" />
                    {lang === 'fr' ? 'Modifier' : 'Edit'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicate(node.id)}>
                    <Copy className="h-3.5 w-3.5 mr-2" />
                    {lang === 'fr' ? 'Dupliquer' : 'Duplicate'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(node.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    {lang === 'fr' ? 'Supprimer' : 'Delete'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Type badge + Title */}
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
              style={{ background: typeConfig.color + '18', border: `1px solid ${typeConfig.color}25` }}
            >
              <TypeIcon className="h-5 w-5" style={{ color: typeConfig.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-cinzel font-bold text-base leading-tight text-foreground mb-1">
                {node.title}
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-md"
                  style={{ background: typeConfig.color + '15', color: typeConfig.color }}
                >
                  {typeConfig.label[lang]}
                </span>
                {node.act && (
                  <span className="text-[10px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-md">
                    {node.act}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Status + Badges row */}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-lg"
              style={{
                background: statusConfig.color + '12',
                color: statusConfig.color,
                border: `1px solid ${statusConfig.color}25`,
              }}
            >
              <span
                className={cn('w-2 h-2 rounded-full', node.status === 'accomplished' && 'animate-pulse')}
                style={{ background: statusConfig.color }}
              />
              {statusConfig.label[lang]}
            </span>
            {node.is_main_plot && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-primary bg-primary/8 border border-primary/15 px-1.5 py-0.5 rounded-md">
                <Star className="h-3 w-3 fill-primary/30" /> {lang === 'fr' ? 'Principal' : 'Main'}
              </span>
            )}
            {node.is_secret && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 dark:text-slate-400 bg-slate-500/8 border border-slate-500/15 px-1.5 py-0.5 rounded-md">
                <Lock className="h-3 w-3" /> Secret
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-5 pb-5 space-y-5">

          {/* Quick status change */}
          {isMJ && (
            <div className="pt-1">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {lang === 'fr' ? 'Changer le statut' : 'Change Status'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(NODE_STATUSES).map(([key, cfg]) => (
                  key !== node.status && (
                    <button
                      key={key}
                      onClick={() => onStatusChange(node.id, key)}
                      className="text-[10px] font-medium px-2.5 py-1 rounded-lg border border-border/60 hover:border-current bg-background/50 hover:bg-current/5 transition-all active:scale-95"
                      style={{ color: cfg.color }}
                    >
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
                        {cfg.label[lang]}
                      </span>
                    </button>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          {node.summary && (
            <div>
              <SectionLabel icon={Eye} label={lang === 'fr' ? 'Résumé' : 'Summary'} />
              <p className="text-sm text-foreground leading-relaxed">{node.summary}</p>
            </div>
          )}

          {/* Description */}
          {node.description && (
            <div>
              <SectionLabel icon={Sparkles} label="Description" />
              <p className="text-[13px] text-foreground/80 leading-relaxed whitespace-pre-wrap">
                {node.description}
              </p>
            </div>
          )}

          {/* Triggers */}
          {node.triggers && (
            <div className="bg-accent/30 rounded-xl p-3.5 border border-border/30">
              <SectionLabel icon={Sparkles} label={lang === 'fr' ? 'Déclencheurs' : 'Triggers'} className="mb-1.5" />
              <p className="text-[13px] text-foreground/80 whitespace-pre-wrap">{node.triggers}</p>
            </div>
          )}

          {/* Consequences */}
          {node.consequences_text && (
            <div className="bg-accent/30 rounded-xl p-3.5 border border-border/30">
              <SectionLabel icon={GitBranch} label={lang === 'fr' ? 'Conséquences' : 'Consequences'} className="mb-1.5" />
              <p className="text-[13px] text-foreground/80 whitespace-pre-wrap">{node.consequences_text}</p>
            </div>
          )}

          {/* MJ Notes */}
          {isMJ && node.mj_notes && (
            <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-3.5">
              <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                {lang === 'fr' ? 'Notes MJ' : 'GM Notes'}
              </p>
              <p className="text-[13px] text-foreground/80 whitespace-pre-wrap">{node.mj_notes}</p>
            </div>
          )}

          {/* ── Linked Quests (enhanced with status) ── */}
          {(linkedQuestsFull.length > 0 || (isMJ && onCreateQuestFromNode)) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <SectionLabel icon={Target} label={lang === 'fr' ? 'Quêtes liées' : 'Linked Quests'} className="mb-0" />
                {isMJ && onCreateQuestFromNode && (
                  <button
                    onClick={() => onCreateQuestFromNode(node)}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                    {lang === 'fr' ? 'Créer quête' : 'Create quest'}
                  </button>
                )}
              </div>

              {linkedQuestsFull.length > 0 ? (
                <div className="space-y-1.5">
                  {linkedQuestsFull.map(quest => {
                    const statusColor = QUEST_STATUS_COLORS[quest.status] || '#6b7280';
                    const statusLabel = QUEST_STATUS_LABELS[quest.status]?.[lang] || quest.status;
                    const completedObj = (quest.objectives || []).filter(o => o.completed).length;
                    const totalObj = (quest.objectives || []).length;
                    return (
                      <Link
                        key={quest.id}
                        to={`/campaigns/${campaignId}/quests/${quest.id}`}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl border border-violet-500/15 bg-violet-500/5 hover:bg-violet-500/10 hover:border-violet-500/25 transition-all group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-violet-500/12 flex items-center justify-center shrink-0">
                          <Target className="h-3.5 w-3.5 text-violet-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-semibold text-foreground truncate block leading-tight">
                            {quest.title}
                          </span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-md"
                              style={{ background: statusColor + '15', color: statusColor }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                              {statusLabel}
                            </span>
                            {totalObj > 0 && (
                              <span className="text-[9px] text-muted-foreground">
                                {completedObj}/{totalObj} obj.
                              </span>
                            )}
                            {quest.quest_type && (
                              <span className="text-[9px] text-muted-foreground/60">
                                {quest.quest_type === 'main' ? '★' : quest.quest_type === 'personal' ? '♦' : '○'}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-violet-500/70 transition-colors shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              ) : isMJ ? (
                <p className="text-[11px] text-muted-foreground/60 italic">
                  {lang === 'fr' ? 'Aucune quête liée à ce nœud.' : 'No quests linked to this node.'}
                </p>
              ) : null}
            </div>
          )}

          {/* Other linked entities */}
          {hasLinkedEntities && (
            <div className="space-y-3">
              <SectionLabel icon={Package} label={lang === 'fr' ? 'Entités liées' : 'Linked Entities'} />
              {node.linked_npcs?.length > 0 && (
                <EntityRow icon={Users} label={lang === 'fr' ? 'PNJ' : 'NPCs'} items={node.linked_npcs} color="#ec4899" />
              )}
              {node.linked_locations?.length > 0 && (
                <EntityRow icon={MapPin} label={lang === 'fr' ? 'Lieux' : 'Locations'} items={node.linked_locations} color="#10b981" />
              )}
              {node.linked_items?.length > 0 && (
                <EntityRow icon={Package} label={lang === 'fr' ? 'Objets' : 'Items'} items={node.linked_items} color="#f59e0b" />
              )}
            </div>
          )}

          {/* Tags */}
          {node.tags?.length > 0 && (
            <div>
              <SectionLabel icon={Tag} label="Tags" />
              <div className="flex flex-wrap gap-1.5">
                {node.tags.map((tag, i) => (
                  <span key={i} className="text-[10px] text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-md border border-border/30">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Connections ── */}
          {(outEdges.length > 0 || inEdges.length > 0) && (
            <div className="space-y-3">
              <SectionLabel icon={GitBranch} label={lang === 'fr' ? 'Connexions' : 'Connections'} />

              {/* Outgoing */}
              {outEdges.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground font-medium mb-1.5">
                    {lang === 'fr' ? 'Branches suivantes' : 'Next Branches'} ({outEdges.length})
                  </p>
                  <div className="space-y-1">
                    {outEdges.map(edge => {
                      const targetType = getNodeType(edge.target_id);
                      const TargetIcon = targetType.icon;
                      return (
                        <button
                          key={edge.id}
                          onClick={() => onSelectNode(edge.target_id)}
                          className="w-full text-left flex items-center gap-2.5 p-2.5 rounded-xl border border-border/40 bg-background/40 hover:bg-accent/50 hover:border-border transition-all group active:scale-[0.98]"
                        >
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: targetType.color + '12' }}
                          >
                            <TargetIcon className="h-3.5 w-3.5" style={{ color: targetType.color }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-semibold text-foreground truncate block leading-tight">
                              {getNodeTitle(edge.target_id)}
                            </span>
                            {edge.label && (
                              <span className="text-[10px] text-muted-foreground truncate block">{edge.label}</span>
                            )}
                          </div>
                          {!edge.is_active && (
                            <span className="text-[9px] text-destructive/70 bg-destructive/8 border border-destructive/15 px-1.5 py-0.5 rounded shrink-0">
                              {lang === 'fr' ? 'Fermé' : 'Closed'}
                            </span>
                          )}
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-foreground/70 transition-colors shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Incoming */}
              {inEdges.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground font-medium mb-1.5">
                    {lang === 'fr' ? 'Venant de' : 'Coming From'} ({inEdges.length})
                  </p>
                  <div className="space-y-1">
                    {inEdges.map(edge => {
                      const sourceType = getNodeType(edge.source_id);
                      const SourceIcon = sourceType.icon;
                      return (
                        <button
                          key={edge.id}
                          onClick={() => onSelectNode(edge.source_id)}
                          className="w-full text-left flex items-center gap-2.5 p-2.5 rounded-xl border border-border/40 bg-background/40 hover:bg-accent/50 hover:border-border transition-all group active:scale-[0.98]"
                        >
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: sourceType.color + '12' }}
                          >
                            <SourceIcon className="h-3.5 w-3.5" style={{ color: sourceType.color }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-semibold text-foreground truncate block leading-tight">
                              {getNodeTitle(edge.source_id)}
                            </span>
                            {edge.label && (
                              <span className="text-[10px] text-muted-foreground truncate block">{edge.label}</span>
                            )}
                          </div>
                          <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-foreground/70 transition-colors shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History timeline */}
          {isMJ && node.history?.length > 0 && (
            <div>
              <SectionLabel icon={Clock} label={lang === 'fr' ? 'Historique' : 'History'} />
              <div className="relative pl-4 space-y-2 mt-1">
                {/* Vertical line */}
                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-border/60" />
                {node.history.slice().reverse().slice(0, 8).map((entry, i) => (
                  <div key={i} className="relative flex items-start gap-2">
                    <span
                      className="absolute left-[-13px] top-[5px] w-2.5 h-2.5 rounded-full border-2 border-background bg-muted-foreground/40 shrink-0"
                      style={i === 0 ? { background: statusConfig.color, borderColor: 'hsl(var(--background))' } : {}}
                    />
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-medium text-foreground/70 capitalize block">
                        {entry.action?.replace(/_/g, ' ')}
                        {entry.from && <span className="text-muted-foreground"> : {entry.from} → {entry.to}</span>}
                      </span>
                      {entry.at && (
                        <span className="text-[9px] text-muted-foreground/60">{formatDateTime(entry.at)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meta footer */}
          <div className="text-[10px] text-muted-foreground/60 space-y-0.5 pt-2 border-t border-border/30">
            {node.arc && <p>Arc: <span className="text-muted-foreground">{node.arc}</span></p>}
            <p>{lang === 'fr' ? 'Créé' : 'Created'}: {formatDateTime(node.created_at)}</p>
            {node.updated_at && <p>{lang === 'fr' ? 'Modifié' : 'Updated'}: {formatDateTime(node.updated_at)}</p>}
          </div>
        </div>
      </ScrollArea>

      {/* ── Quick edit footer (mobile) ── */}
      {isMJ && (
        <div className="px-5 py-3 border-t border-border/40 shrink-0 flex gap-2 sm:hidden">
          <Button size="sm" className="flex-1 text-xs gap-1.5" onClick={() => onEdit(node)}>
            <Edit2 className="h-3 w-3" /> {lang === 'fr' ? 'Modifier' : 'Edit'}
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onDuplicate(node.id)}>
            <Copy className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(node.id)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl p-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mt-2 mb-0" />
          {panelContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="flex narrative-details-panel w-[400px] border-l border-border/50 bg-card/95 backdrop-blur-sm flex-col h-full animate-slide-in-right">
      {panelContent}
    </div>
  );
}

/* ── Helpers ── */

function SectionLabel({ icon: Icon, label, className }) {
  return (
    <p className={cn('text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5', className)}>
      <Icon className="h-3 w-3" />
      {label}
    </p>
  );
}

function EntityRow({ icon: Icon, label, items, color }) {
  return (
    <div className="flex items-start gap-2.5">
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: color + '12' }}
      >
        <Icon className="h-3 w-3" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-[10px] text-muted-foreground">{label}</span>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {items.map((id, i) => (
            <span key={i} className="text-[10px] bg-muted/50 text-foreground/70 px-1.5 py-0.5 rounded-md border border-border/30">
              {id}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
